import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type Dispatch,
  type ReactNode,
} from 'react'
import type {
  AttestationState,
  AuthorizationState,
  BusinessEntity,
  ClientContacts,
  CredentialRecord,
  Drlp,
  IndividualLicensee,
  OnboardingState,
  UploadedFileRef,
  WizardLocation,
  WritingBusinessAnswer,
} from '../types/onboarding'
import { MOCK_CLIENT_NAME, createSeedEntities } from '../data/mockSeed'
import { createEmptyDrlp } from '../lib/factories'
import { syncEntityCredentials } from '../lib/credentials'
import {
  ENTITY_SECTIONS,
  clientStepProgress,
  entityPercent,
  entitySectionProgress,
} from '../lib/completion'
import { ENFORCE_REQUIRED_FIELDS } from '../lib/prototypeSettings'
import { CLIENT_STEP_ORDER, clientLocation, nextLocation, overallPercent, prevLocation } from '../wizard/flow'

const STORAGE_KEY = 'misc_onboarding_prototype_state'
const STATE_VERSION = 3

function createInitialState(): OnboardingState {
  return {
    version: STATE_VERSION,
    clientName: MOCK_CLIENT_NAME,
    startedAt: new Date().toISOString(),
    location: clientLocation('welcome'),
    visitedSteps: ['welcome'],
    importantAcknowledged: false,
    entities: createSeedEntities(),
    contacts: { invoiceEmails: '', portalAdminEmails: '', taxPaymentEmails: '' },
    attestation: { checked: false, initials: '' },
    implementation: { signature: null, authorizations: {} },
    otrUpload: null,
    certified: false,
    submittedAt: null,
    toast: null,
  }
}

/** Save-and-resume (design manual): progress is kept in this browser between visits. */
function loadState(): OnboardingState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return createInitialState()
    const parsed = JSON.parse(raw) as OnboardingState
    if (parsed?.version !== STATE_VERSION) return createInitialState()
    return { ...parsed, toast: null }
  } catch {
    return createInitialState()
  }
}

function saveState(state: OnboardingState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, toast: null }))
  } catch {
    // private mode / quota — the demo still works in memory
  }
}

export function readSavedOnboardingState(): OnboardingState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? (JSON.parse(raw) as OnboardingState) : null
    return parsed?.version === STATE_VERSION ? parsed : null
  } catch {
    return null
  }
}

export type EntityDetailsPatch = Partial<Pick<BusinessEntity, 'name' | 'residentState' | 'slLicenseNumber' | 'npn' | 'fein'>>

export type OnboardingAction =
  | { type: 'GO'; location: WizardLocation }
  | { type: 'SET_IMPORTANT_ACK'; value: boolean }
  | { type: 'ADD_ENTITIES'; entities: BusinessEntity[] }
  | { type: 'UPDATE_ENTITY'; entityId: string; patch: EntityDetailsPatch }
  | { type: 'REMOVE_ENTITY'; entityId: string }
  | { type: 'SET_ENTITY_STATES'; entityId: string; states: string[] }
  | { type: 'ADD_DRLP'; entityId: string }
  | { type: 'UPDATE_DRLP'; entityId: string; drlpId: string; patch: Partial<Omit<Drlp, 'id'>> }
  | { type: 'REMOVE_DRLP'; entityId: string; drlpId: string }
  | { type: 'TOGGLE_DRLP_STATE'; entityId: string; drlpId: string; stateCode: string }
  | { type: 'ADD_INDIVIDUALS'; entityId: string; individuals: IndividualLicensee[] }
  | { type: 'UPDATE_INDIVIDUAL'; entityId: string; individualId: string; patch: Partial<Omit<IndividualLicensee, 'id'>> }
  | { type: 'REMOVE_INDIVIDUAL'; entityId: string; individualId: string }
  | { type: 'MARK_INDIVIDUALS_REVIEWED'; entityId: string }
  | { type: 'SET_WRITING_BUSINESS'; entityId: string; stateCode: string; answer: WritingBusinessAnswer }
  | { type: 'ADD_CREDENTIALS'; entityId: string; rows: CredentialRecord[] }
  | { type: 'UPDATE_CREDENTIAL'; entityId: string; credentialId: string; patch: Partial<Omit<CredentialRecord, 'id' | 'portalId'>> }
  | { type: 'REMOVE_CREDENTIAL'; entityId: string; credentialId: string }
  | { type: 'SET_CONTACT'; field: keyof ClientContacts; value: string }
  | { type: 'SET_ATTESTATION'; patch: Partial<AttestationState> }
  | { type: 'SET_SIGNATURE'; file: UploadedFileRef | null }
  | { type: 'SET_AUTHORIZATION'; stateCode: AuthorizationState; file: UploadedFileRef | null }
  | { type: 'SET_OTR_UPLOAD'; file: UploadedFileRef | null }
  | { type: 'SET_CERTIFIED'; value: boolean }
  | { type: 'SUBMIT' }
  | { type: 'TOAST'; message: string }
  | { type: 'CLEAR_TOAST' }
  | { type: 'RESET' }

function withEntity(
  state: OnboardingState,
  entityId: string,
  fn: (e: BusinessEntity) => BusinessEntity,
): OnboardingState {
  return {
    ...state,
    entities: state.entities.map((e) =>
      e.id === entityId ? syncEntityCredentials({ ...fn(e), updatedAt: new Date().toISOString() }) : e,
    ),
  }
}

function withoutPersonCredentials(e: BusinessEntity, personId: string): CredentialRecord[] {
  return e.credentials.filter((c) => c.personId !== personId)
}

function reducer(state: OnboardingState, action: OnboardingAction): OnboardingState {
  switch (action.type) {
    case 'GO': {
      const loc = action.location
      const visitedSteps =
        loc.kind === 'client' && !state.visitedSteps.includes(loc.step)
          ? [...state.visitedSteps, loc.step]
          : state.visitedSteps
      return { ...state, location: loc, visitedSteps }
    }
    case 'SET_IMPORTANT_ACK':
      return { ...state, importantAcknowledged: action.value }

    case 'ADD_ENTITIES':
      return { ...state, entities: [...state.entities, ...action.entities] }
    case 'UPDATE_ENTITY':
      return withEntity(state, action.entityId, (e) => ({ ...e, ...action.patch }))
    case 'REMOVE_ENTITY':
      return { ...state, entities: state.entities.filter((e) => e.id !== action.entityId) }

    case 'SET_ENTITY_STATES': {
      const states = [...action.states].sort()
      return withEntity(state, action.entityId, (e) => ({
        ...e,
        statesToHandle: states,
        drlps: e.drlps.map((d) => ({ ...d, assignedStates: d.assignedStates.filter((s) => states.includes(s)) })),
        writingBusiness: Object.fromEntries(
          Object.entries(e.writingBusiness).filter(([code]) => states.includes(code)),
        ),
      }))
    }

    case 'ADD_DRLP':
      return withEntity(state, action.entityId, (e) => ({ ...e, drlps: [...e.drlps, createEmptyDrlp()] }))
    case 'UPDATE_DRLP':
      return withEntity(state, action.entityId, (e) => ({
        ...e,
        drlps: e.drlps.map((d) => (d.id === action.drlpId ? { ...d, ...action.patch } : d)),
      }))
    case 'REMOVE_DRLP':
      return withEntity(state, action.entityId, (e) => ({
        ...e,
        drlps: e.drlps.filter((d) => d.id !== action.drlpId),
        credentials: withoutPersonCredentials(e, action.drlpId),
      }))
    case 'TOGGLE_DRLP_STATE':
      return withEntity(state, action.entityId, (e) => ({
        ...e,
        drlps: e.drlps.map((d) => {
          if (d.id !== action.drlpId) return d
          const has = d.assignedStates.includes(action.stateCode)
          return {
            ...d,
            assignedStates: has
              ? d.assignedStates.filter((s) => s !== action.stateCode)
              : [...d.assignedStates, action.stateCode].sort(),
          }
        }),
      }))

    case 'ADD_INDIVIDUALS':
      return withEntity(state, action.entityId, (e) => ({ ...e, individuals: [...e.individuals, ...action.individuals] }))
    case 'UPDATE_INDIVIDUAL':
      return withEntity(state, action.entityId, (e) => ({
        ...e,
        individuals: e.individuals.map((p) => (p.id === action.individualId ? { ...p, ...action.patch } : p)),
      }))
    case 'REMOVE_INDIVIDUAL':
      return withEntity(state, action.entityId, (e) => ({
        ...e,
        individuals: e.individuals.filter((p) => p.id !== action.individualId),
        credentials: withoutPersonCredentials(e, action.individualId),
      }))
    case 'MARK_INDIVIDUALS_REVIEWED':
      return withEntity(state, action.entityId, (e) => ({ ...e, individualsReviewed: true }))

    case 'SET_WRITING_BUSINESS':
      return withEntity(state, action.entityId, (e) => ({
        ...e,
        writingBusiness: { ...e.writingBusiness, [action.stateCode]: action.answer },
      }))
    case 'ADD_CREDENTIALS':
      return withEntity(state, action.entityId, (e) => ({ ...e, credentials: [...e.credentials, ...action.rows] }))
    case 'UPDATE_CREDENTIAL':
      return withEntity(state, action.entityId, (e) => ({
        ...e,
        credentials: e.credentials.map((c) => (c.id === action.credentialId ? { ...c, ...action.patch } : c)),
      }))
    case 'REMOVE_CREDENTIAL':
      return withEntity(state, action.entityId, (e) => ({
        ...e,
        credentials: e.credentials.filter((c) => c.id !== action.credentialId),
      }))

    case 'SET_CONTACT':
      return { ...state, contacts: { ...state.contacts, [action.field]: action.value } }
    case 'SET_ATTESTATION':
      return { ...state, attestation: { ...state.attestation, ...action.patch } }
    case 'SET_SIGNATURE':
      return { ...state, implementation: { ...state.implementation, signature: action.file } }
    case 'SET_AUTHORIZATION': {
      const authorizations = { ...state.implementation.authorizations }
      if (action.file) authorizations[action.stateCode] = action.file
      else delete authorizations[action.stateCode]
      return { ...state, implementation: { ...state.implementation, authorizations } }
    }
    case 'SET_OTR_UPLOAD':
      return { ...state, otrUpload: action.file }
    case 'SET_CERTIFIED':
      return { ...state, certified: action.value }
    case 'SUBMIT':
      return { ...state, submittedAt: new Date().toISOString() }

    case 'TOAST':
      return { ...state, toast: action.message }
    case 'CLEAR_TOAST':
      return { ...state, toast: null }
    case 'RESET':
      return createInitialState()
  }
}

interface OnboardingContextValue {
  state: OnboardingState
  dispatch: Dispatch<OnboardingAction>
  location: WizardLocation
  /** Entity being worked on when inside the entity workflow. */
  currentEntity: BusinessEntity | null
  goTo: (location: WizardLocation) => void
  advance: () => void
  back: () => void
  canGoBack: boolean
  /** Items still missing on the current page. */
  blockers: string[]
  /** True after Continue was pressed on this page while required items were missing. */
  showBlockers: boolean
  /** Whether a page can be opened directly (progress bar, section tabs) — every earlier step must be complete. */
  canReach: (location: WizardLocation) => boolean
  percentComplete: number
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null)

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadState)

  useEffect(() => {
    saveState(state)
  }, [state])

  const location = state.location
  const currentEntity =
    location.kind === 'entity' ? state.entities.find((e) => e.id === location.entityId) ?? null : null

  // An entity removed from the list while it was open → back to the dashboard.
  useEffect(() => {
    if (location.kind === 'entity' && !currentEntity) {
      dispatch({ type: 'GO', location: clientLocation('entityDashboard') })
    }
  }, [location, currentEntity])

  const blockers = useMemo(() => {
    if (location.kind === 'entity') {
      return currentEntity ? entitySectionProgress(currentEntity, location.section).missing : []
    }
    return clientStepProgress(state, location.step).missing
  }, [state, location, currentEntity])

  const blocked = ENFORCE_REQUIRED_FIELDS && blockers.length > 0

  // Missing items are only listed after the user tries to continue; reset when the page changes.
  const [attemptedPage, setAttemptedPage] = useState<string | null>(null)
  const pageKey = JSON.stringify(location)
  const showBlockers = blocked && attemptedPage === pageKey

  const canReach = useCallback(
    (loc: WizardLocation) => {
      if (!ENFORCE_REQUIRED_FIELDS) return true
      const stepDone = (step: (typeof CLIENT_STEP_ORDER)[number]) => clientStepProgress(state, step).missing.length === 0
      if (loc.kind === 'entity') {
        const entity = state.entities.find((e) => e.id === loc.entityId)
        if (!entity) return false
        const before = CLIENT_STEP_ORDER.slice(0, CLIENT_STEP_ORDER.indexOf('entityDashboard'))
        const sectionsBefore = ENTITY_SECTIONS.slice(0, ENTITY_SECTIONS.indexOf(loc.section))
        return before.every(stepDone) && sectionsBefore.every((s) => entitySectionProgress(entity, s).missing.length === 0)
      }
      if (loc.step === 'complete') return Boolean(state.submittedAt)
      return CLIENT_STEP_ORDER.slice(0, CLIENT_STEP_ORDER.indexOf(loc.step)).every(stepDone)
    },
    [state],
  )

  const goTo = useCallback((loc: WizardLocation) => {
    dispatch({ type: 'GO', location: loc })
    window.scrollTo({ top: 0 })
  }, [])

  const advance = useCallback(() => {
    if (blocked) {
      setAttemptedPage(pageKey)
      return
    }
    if (location.kind === 'entity' && currentEntity) {
      if (location.section === 'individuals') {
        dispatch({ type: 'MARK_INDIVIDUALS_REVIEWED', entityId: currentEntity.id })
      }
      if (location.section === 'credentials') {
        const pct = entityPercent(currentEntity)
        dispatch({
          type: 'TOAST',
          message: `${currentEntity.name || 'Entity'} saved — ${pct}% complete. Choose the next entity to work on.`,
        })
      }
    }
    if (location.kind === 'client' && location.step === 'review') {
      dispatch({ type: 'SUBMIT' })
    }
    goTo(nextLocation(location))
  }, [blocked, pageKey, location, currentEntity, goTo])

  const prev = prevLocation(location)
  const back = useCallback(() => {
    if (prev) goTo(prev)
  }, [prev, goTo])

  const value = useMemo<OnboardingContextValue>(
    () => ({
      state,
      dispatch,
      location,
      currentEntity,
      goTo,
      advance,
      back,
      canGoBack: prev !== null,
      blockers,
      showBlockers,
      canReach,
      percentComplete: overallPercent(state),
    }),
    [state, location, currentEntity, goTo, advance, back, prev, blockers, showBlockers, canReach],
  )

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext)
  if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider')
  return ctx
}
